/**
 * The round draft — what the user has picked so far, in memory only (spec
 * "Frontend": drafts live in Zustand; a reload starts empty). Pure reducers,
 * so the card stays a thin view and every rule is unit-tested here.
 *
 * Rules (spec "Interaction"):
 * - single-select is a radio: a tap replaces, a second tap keeps;
 * - multi-select ticks toggle and are kept in the ROUND's option order, the
 *   order the request sends and the summary prints (N2);
 * - «Не знаю» is exclusive with everything else;
 * - «Свой ответ…» is exclusive in single-select and sits alongside the ticks in
 *   multi-select; `custom === null` means "not chosen", `""` "chosen, empty";
 * - a question with nothing usable is simply absent from the request — that
 *   is what moving forward without answering means on the wire.
 */
import type { ClarifyAnswerInput, ClarifyRoundEvent, ClarifyRoundQuestion } from "./types";

export interface QuestionDraft {
  /** Chosen option ids, in the round's option order. */
  opts: string[];
  dontKnow: boolean;
  /** «Свой ответ…»: null = not chosen; "" = chosen, nothing typed yet. */
  custom: string | null;
}

export interface ClarifyDraft {
  roundId: string;
  /** The question on screen. */
  index: number;
  /** By question id; an absent question is untouched. */
  answers: Record<string, QuestionDraft>;
}

/** One row of a question: a vocabulary option, «Не знаю» or «Свой ответ…». */
export type ClarifyChoice =
  | { kind: "option"; optionId: string }
  | { kind: "dontKnow" }
  | { kind: "custom" };

function blank(): QuestionDraft {
  return { opts: [], dontKnow: false, custom: null };
}

function inOptionOrder(question: ClarifyRoundQuestion, ids: Iterable<string>): string[] {
  const chosen = new Set(ids);
  return question.options.filter((option) => chosen.has(option.id)).map((option) => option.id);
}

export function emptyDraft(round: ClarifyRoundEvent): ClarifyDraft {
  return { roundId: round.round_id, index: 0, answers: {} };
}

/** The draft for `round`: the stored one if it belongs to it, else a fresh one. */
export function draftFor(round: ClarifyRoundEvent, draft: ClarifyDraft | null): ClarifyDraft {
  return draft !== null && draft.roundId === round.round_id ? draft : emptyDraft(round);
}

export function questionDraft(draft: ClarifyDraft, questionId: string): QuestionDraft {
  return draft.answers[questionId] ?? blank();
}

export function withAnswer(draft: ClarifyDraft, questionId: string, answer: QuestionDraft): ClarifyDraft {
  return { ...draft, answers: { ...draft.answers, [questionId]: answer } };
}

export function withIndex(draft: ClarifyDraft, index: number): ClarifyDraft {
  return { ...draft, index };
}

/** The rows of a question: its options, then «Не знаю» and «Свой ответ…» if allowed. */
export function buildChoices(question: ClarifyRoundQuestion): ClarifyChoice[] {
  const choices: ClarifyChoice[] = question.options.map((option) => ({
    kind: "option",
    optionId: option.id,
  }));
  if (question.allow_dont_know) choices.push({ kind: "dontKnow" });
  if (question.allow_custom) choices.push({ kind: "custom" });
  return choices;
}

export function applyChoice(
  question: ClarifyRoundQuestion,
  current: QuestionDraft,
  choice: ClarifyChoice,
): QuestionDraft {
  if (choice.kind === "option") {
    if (!question.multi) return { opts: [choice.optionId], dontKnow: false, custom: null };
    const ids = new Set(current.opts);
    if (ids.has(choice.optionId)) ids.delete(choice.optionId);
    else ids.add(choice.optionId);
    return { opts: inOptionOrder(question, ids), dontKnow: false, custom: current.custom };
  }
  if (choice.kind === "dontKnow") {
    if (question.multi && current.dontKnow) return blank();
    return { opts: [], dontKnow: true, custom: null };
  }
  if (!question.multi) return { opts: [], dontKnow: false, custom: current.custom ?? "" };
  return { opts: current.opts, dontKnow: false, custom: current.custom === null ? "" : null };
}

/** Typed «Свой ответ» text, capped at the server's `custom_max_chars`. */
export function setCustomText(current: QuestionDraft, text: string, max: number): QuestionDraft {
  return { ...current, custom: text.slice(0, max) };
}

export function isChoiceChecked(answer: QuestionDraft, choice: ClarifyChoice): boolean {
  if (choice.kind === "option") return answer.opts.includes(choice.optionId);
  if (choice.kind === "dontKnow") return answer.dontKnow;
  return answer.custom !== null;
}

export function isAnswered(answer: QuestionDraft): boolean {
  return (
    answer.dontKnow ||
    answer.opts.length > 0 ||
    (answer.custom !== null && answer.custom.trim().length > 0)
  );
}

/** The `clarify_answers` of a submission (contract C7), in round order. */
export function toAnswerInputs(round: ClarifyRoundEvent, draft: ClarifyDraft): ClarifyAnswerInput[] {
  const inputs: ClarifyAnswerInput[] = [];
  for (const question of round.questions) {
    const answer = draft.answers[question.id];
    if (answer === undefined || !isAnswered(answer)) continue;
    if (answer.dontKnow) {
      inputs.push({ q: question.id, opts: [], dont_know: true });
      continue;
    }
    const custom = answer.custom?.trim() ?? "";
    inputs.push({
      q: question.id,
      opts: inOptionOrder(question, answer.opts),
      ...(custom.length > 0 ? { custom } : {}),
    });
  }
  return inputs;
}
